import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Clock, Receipt, Car, Tag, TrendingUp, TrendingDown,
  ExternalLink, Save,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => {
  const v = Number(n) || 0;
  const neg = v < 0;
  return `${neg ? '−' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const today = () => new Date().toISOString().slice(0, 10);

const TIME_CATS = [
  { value: 'onsite', label: 'Onsite' },
  { value: 'admin', label: 'Admin / Writing estimate' },
  { value: 'drive', label: 'Driving' },
  { value: 'client_meeting', label: 'Client meeting' },
];
const MAT_CATS = [
  { value: 'lumber', label: 'Lumber' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'tools', label: 'Tools / rentals' },
  { value: 'other', label: 'Other' },
]

export default function JobCostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ default_hourly_rate: 65, default_km_rate: 0.72 });
  const [leadDraft, setLeadDraft] = useState({ lead_source: '', lead_cost: '', lead_date: '' });

  const fetchDetail = useCallback(async () => {
    try {
      const res = await fetch(`${API}/job-costs/${id}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setLeadDraft({
          lead_source: d.lead_source || '',
          lead_cost: d.lead_cost || '',
          lead_date: d.lead_date || '',
        });
      } else {
        navigate('/job-costs');
      }
    } catch {
      navigate('/job-costs');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
    fetch(`${API}/job-costs/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(s => s && setSettings(s))
      .catch(() => {});
  }, [fetchDetail]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { estimate, summary, time_entries, material_receipts, mileage_entries } = data;
  const clientDisplay = estimate.client?.company || estimate.client?.name || '—';
  const isProfit = summary.profit >= 0;

  // ── Time entries ─────────────────────────────────────────────
  const addTimeEntry = async () => {
    try {
      const res = await fetch(`${API}/job-costs/${id}/time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: today(),
          category: 'onsite',
          hours: 0,
          hourly_rate: settings.default_hourly_rate,
          note: '',
        }),
      });
      if (res.ok) fetchDetail();
    } catch { toast.error('Failed to add'); }
  };

  const updateTimeEntry = async (entryId, patch) => {
    try {
      const res = await fetch(`${API}/job-costs/time/${entryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (res.ok) fetchDetail();
    } catch { toast.error('Failed to update'); }
  };

  const deleteTimeEntry = async (entryId) => {
    try {
      const res = await fetch(`${API}/job-costs/time/${entryId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) fetchDetail();
    } catch { /* ignore */ }
  };

  // ── Material receipts ────────────────────────────────────────
  const addReceipt = async () => {
    try {
      const res = await fetch(`${API}/job-costs/${id}/materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: today(), vendor: '', amount: 0, category: 'other', note: '' }),
      });
      if (res.ok) fetchDetail();
    } catch { toast.error('Failed to add'); }
  };

  const updateReceipt = async (receiptId, patch) => {
    try {
      const res = await fetch(`${API}/job-costs/materials/${receiptId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (res.ok) fetchDetail();
    } catch { toast.error('Failed to update'); }
  };

  const deleteReceipt = async (receiptId) => {
    try {
      const res = await fetch(`${API}/job-costs/materials/${receiptId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) fetchDetail();
    } catch { /* ignore */ }
  };

  // ── Mileage ──────────────────────────────────────────────────
  const addMileage = async () => {
    try {
      const res = await fetch(`${API}/job-costs/${id}/mileage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: today(), km: 0, rate_per_km: settings.default_km_rate, fuel_cost: 0, note: '',
        }),
      });
      if (res.ok) fetchDetail();
    } catch { toast.error('Failed to add'); }
  };

  const updateMileage = async (mId, patch) => {
    try {
      const res = await fetch(`${API}/job-costs/mileage/${mId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (res.ok) fetchDetail();
    } catch { toast.error('Failed to update'); }
  };

  const deleteMileage = async (mId) => {
    try {
      const res = await fetch(`${API}/job-costs/mileage/${mId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) fetchDetail();
    } catch { /* ignore */ }
  };

  // ── Lead cost ────────────────────────────────────────────────
  const saveLead = async () => {
    try {
      const res = await fetch(`${API}/job-costs/${id}/lead`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lead_source: leadDraft.lead_source,
          lead_cost: parseFloat(leadDraft.lead_cost) || 0,
          lead_date: leadDraft.lead_date,
        }),
      });
      if (res.ok) { toast.success('Lead info saved'); fetchDetail(); }
    } catch { toast.error('Failed to save'); }
  };

  return (
    <div className="animate-fade-in space-y-6" data-testid="job-cost-detail">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Button
            variant="ghost"
            onClick={() => navigate('/job-costs')}
            className="text-[#6B7A73] hover:bg-[#F0F3F1] -ml-2"
            data-testid="job-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <p className="text-xs text-[#6B7A73]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {estimate.estimate_number} · {estimate.status?.toUpperCase()}
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold text-[#1A1D1C] mt-0.5" style={{ fontFamily: "'Chivo', sans-serif" }}>
              {estimate.title || 'Untitled'}
            </h1>
            <p className="text-sm text-[#6B7A73] mt-0.5">{clientDisplay}</p>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/estimates/${estimate.estimate_id}`)}
          variant="outline"
          className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md"
          data-testid="open-estimate-btn"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Open estimate
        </Button>
      </div>

      {/* Profit card */}
      <div className={`border rounded-md p-5 ${isProfit ? 'bg-emerald-50/40 border-emerald-200' : 'bg-red-50/40 border-red-200'}`}>
        <div className="flex items-center gap-2 mb-3">
          {isProfit ? <TrendingUp className="w-4 h-4 text-emerald-700" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
          <h2 className={`text-sm font-semibold uppercase tracking-[0.15em] ${isProfit ? 'text-emerald-900' : 'text-red-900'}`}>
            {isProfit ? 'Profit Snapshot' : 'Running at a Loss'}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <MiniStat label="Revenue" value={fmt(summary.revenue)} />
          <MiniStat label="Labour" value={fmt(summary.labour)} muted />
          <MiniStat label="Materials" value={fmt(summary.materials)} muted />
          <MiniStat label="Mileage" value={fmt(summary.mileage)} muted />
          <MiniStat label="Lead cost" value={fmt(summary.lead_cost)} muted />
          <MiniStat
            label={isProfit ? `Profit · ${summary.margin_pct.toFixed(1)}%` : `Loss · ${summary.margin_pct.toFixed(1)}%`}
            value={fmt(summary.profit)}
            tone={isProfit ? 'profit' : 'loss'}
          />
        </div>
        <p className="text-xs text-[#6B7A73] mt-3">
          Revenue includes accepted change orders (pre-tax). Taxes remitted to CRA/RQ pass through and are excluded.
        </p>
      </div>

      {/* Lead cost */}
      <Section icon={Tag} title="Lead Source">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-[#6B7A73] block mb-1">Platform</label>
            <Input
              value={leadDraft.lead_source}
              onChange={e => setLeadDraft({ ...leadDraft, lead_source: e.target.value })}
              placeholder="Homestars, HomeAdvisor, Referral..."
              className="bg-white border-[#E6EBE8]"
              data-testid="lead-source-input"
            />
          </div>
          <div>
            <label className="text-xs text-[#6B7A73] block mb-1">Cost ($)</label>
            <Input
              type="number"
              step="0.01"
              value={leadDraft.lead_cost}
              onChange={e => setLeadDraft({ ...leadDraft, lead_cost: e.target.value })}
              placeholder="0.00"
              className="bg-white border-[#E6EBE8]"
              data-testid="lead-cost-input"
            />
          </div>
          <div>
            <label className="text-xs text-[#6B7A73] block mb-1">Date</label>
            <Input
              type="date"
              value={leadDraft.lead_date}
              onChange={e => setLeadDraft({ ...leadDraft, lead_date: e.target.value })}
              className="bg-white border-[#E6EBE8]"
              data-testid="lead-date-input"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={saveLead}
              className="bg-[#2D6B68] text-white hover:bg-[#235552] w-full"
              data-testid="save-lead-btn"
            >
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </div>
        </div>
      </Section>

      {/* Time entries */}
      <Section
        icon={Clock}
        title="Time"
        onAdd={addTimeEntry}
        addTestId="add-time-btn"
        count={time_entries.length}
      >
        {time_entries.length === 0 ? (
          <Empty text="No time logged yet. Click Add to start tracking." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#6B7A73]">
                  <th className="text-left pb-2 font-semibold w-32">Date</th>
                  <th className="text-left pb-2 font-semibold w-40">Category</th>
                  <th className="text-right pb-2 font-semibold w-20">Hrs</th>
                  <th className="text-right pb-2 font-semibold w-24">Rate</th>
                  <th className="text-right pb-2 font-semibold w-28">Cost</th>
                  <th className="text-left pb-2 font-semibold">Note</th>
                  <th className="w-10 pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F3F1]">
                {time_entries.map(t => {
                  const cost = (t.hours || 0) * (t.hourly_rate || 0);
                  return (
                    <tr key={t.time_entry_id}>
                      <td className="py-1.5 pr-2">
                        <Input type="date" value={t.date} onChange={e => updateTimeEntry(t.time_entry_id, { date: e.target.value })}
                          className="h-8 text-sm border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 pr-2">
                        <Select value={t.category} onValueChange={v => updateTimeEntry(t.time_entry_id, { category: v })}>
                          <SelectTrigger className="h-8 text-sm border-[#E6EBE8]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TIME_CATS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input type="number" step="0.25" value={t.hours}
                          onChange={e => updateTimeEntry(t.time_entry_id, { hours: parseFloat(e.target.value) || 0 })}
                          className="h-8 text-sm text-right border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input type="number" step="0.01" value={t.hourly_rate}
                          onChange={e => updateTimeEntry(t.time_entry_id, { hourly_rate: parseFloat(e.target.value) || 0 })}
                          className="h-8 text-sm text-right border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 pr-2 text-right text-sm font-medium" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {fmt(cost)}
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input value={t.note || ''} onChange={e => updateTimeEntry(t.time_entry_id, { note: e.target.value })}
                          placeholder="Optional note"
                          className="h-8 text-sm border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 text-right">
                        <button onClick={() => deleteTimeEntry(t.time_entry_id)}
                          className="p-1 text-[#6B7A73] hover:text-red-600 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Materials */}
      <Section
        icon={Receipt}
        title="Materials & Purchases"
        onAdd={addReceipt}
        addTestId="add-receipt-btn"
        count={material_receipts.length}
      >
        {material_receipts.length === 0 ? (
          <Empty text="No receipts logged yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#6B7A73]">
                  <th className="text-left pb-2 font-semibold w-32">Date</th>
                  <th className="text-left pb-2 font-semibold w-40">Vendor</th>
                  <th className="text-left pb-2 font-semibold w-36">Category</th>
                  <th className="text-right pb-2 font-semibold w-28">Amount</th>
                  <th className="text-left pb-2 font-semibold">Note</th>
                  <th className="w-10 pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F3F1]">
                {material_receipts.map(r => (
                  <tr key={r.receipt_id}>
                    <td className="py-1.5 pr-2">
                      <Input type="date" value={r.date} onChange={e => updateReceipt(r.receipt_id, { date: e.target.value })}
                        className="h-8 text-sm border-[#E6EBE8]" />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input value={r.vendor || ''} onChange={e => updateReceipt(r.receipt_id, { vendor: e.target.value })}
                        placeholder="Home Depot, RONA..."
                        className="h-8 text-sm border-[#E6EBE8]" />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Select value={r.category} onValueChange={v => updateReceipt(r.receipt_id, { category: v })}>
                        <SelectTrigger className="h-8 text-sm border-[#E6EBE8]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MAT_CATS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input type="number" step="0.01" value={r.amount}
                        onChange={e => updateReceipt(r.receipt_id, { amount: parseFloat(e.target.value) || 0 })}
                        className="h-8 text-sm text-right border-[#E6EBE8]" />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input value={r.note || ''} onChange={e => updateReceipt(r.receipt_id, { note: e.target.value })}
                        placeholder="Optional note"
                        className="h-8 text-sm border-[#E6EBE8]" />
                    </td>
                    <td className="py-1.5 text-right">
                      <button onClick={() => deleteReceipt(r.receipt_id)}
                        className="p-1 text-[#6B7A73] hover:text-red-600 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Mileage */}
      <Section
        icon={Car}
        title="Mileage & Fuel"
        onAdd={addMileage}
        addTestId="add-mileage-btn"
        count={mileage_entries.length}
      >
        {mileage_entries.length === 0 ? (
          <Empty text="No trips logged yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#6B7A73]">
                  <th className="text-left pb-2 font-semibold w-32">Date</th>
                  <th className="text-right pb-2 font-semibold w-20">km</th>
                  <th className="text-right pb-2 font-semibold w-24">$/km</th>
                  <th className="text-right pb-2 font-semibold w-28">Fuel ($)</th>
                  <th className="text-right pb-2 font-semibold w-28">Total</th>
                  <th className="text-left pb-2 font-semibold">Note</th>
                  <th className="w-10 pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F3F1]">
                {mileage_entries.map(m => {
                  const total = (m.km || 0) * (m.rate_per_km || 0) + (m.fuel_cost || 0);
                  return (
                    <tr key={m.mileage_id}>
                      <td className="py-1.5 pr-2">
                        <Input type="date" value={m.date} onChange={e => updateMileage(m.mileage_id, { date: e.target.value })}
                          className="h-8 text-sm border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input type="number" step="0.1" value={m.km}
                          onChange={e => updateMileage(m.mileage_id, { km: parseFloat(e.target.value) || 0 })}
                          className="h-8 text-sm text-right border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input type="number" step="0.01" value={m.rate_per_km}
                          onChange={e => updateMileage(m.mileage_id, { rate_per_km: parseFloat(e.target.value) || 0 })}
                          className="h-8 text-sm text-right border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input type="number" step="0.01" value={m.fuel_cost}
                          onChange={e => updateMileage(m.mileage_id, { fuel_cost: parseFloat(e.target.value) || 0 })}
                          className="h-8 text-sm text-right border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 pr-2 text-right text-sm font-medium" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {fmt(total)}
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input value={m.note || ''} onChange={e => updateMileage(m.mileage_id, { note: e.target.value })}
                          placeholder="Optional note"
                          className="h-8 text-sm border-[#E6EBE8]" />
                      </td>
                      <td className="py-1.5 text-right">
                        <button onClick={() => deleteMileage(m.mileage_id)}
                          className="p-1 text-[#6B7A73] hover:text-red-600 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children, onAdd, addTestId, count }) {
  return (
    <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#2D6B68]" />
          <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">{title}</h2>
          {typeof count === 'number' && count > 0 && (
            <span className="text-xs text-[#6B7A73]">({count})</span>
          )}
        </div>
        {onAdd && (
          <Button
            onClick={onAdd}
            variant="outline"
            size="sm"
            className="border-[#E6EBE8] text-[#2D6B68] hover:bg-[#F0F3F1]"
            data-testid={addTestId}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-[#6B7A73] py-6 text-center">{text}</p>;
}

function MiniStat({ label, value, tone = 'default', muted = false }) {
  const toneClass = tone === 'profit' ? 'text-emerald-700'
    : tone === 'loss' ? 'text-red-600'
    : muted ? 'text-[#6B7A73]' : 'text-[#1A1D1C]';
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7A73] font-semibold">{label}</p>
      <p className={`text-base font-bold ${toneClass}`} style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        {value}
      </p>
    </div>
  );
}
